import _debounce from 'lodash.debounce';

/**
 * Automatically aligns horizontally scrollable elements' start position to match a target
 * container class.
 *
 * THE PROBLEM:
 * We want to have horizontal scrolling on a <div> element that breaks out of the .container class,
 * while also being left-aligned with the container. To do this using just CSS does not work because
 * .container elements are aligned to the centre (margin: auto) and include vw padding, so we won't
 * be able to get to correct relative value. You can get approximations, but as the viewport grows
 * it will be misaligned.
 *
 * THE SOLUTION:
 * Using JS to calculate the absolute left distance of the container we want to match with (our reference)
 * we can then update the left padding of our scrollable div element. This will make it aligned with
 * the container while allowing horizontal scroll across the screen.
 * This is updated on browser resize.
 *
 * Example usage:
 *
 * <div class="scrollable-div  js-hscroll-element" data-hscroll-id="my-custom-reference">
 *   ... [list of scrollable items]
 * </div>
 *
 * <div class="container  js-hscroll-reference" data-hscroll-id="my-custom-reference">
 *   ... [some element that uses the .container class we want to use as a reference]
 * </div>
 *
 * If we need to use an empty container element, we would hide it:
 * <div class="container  js-hscroll-reference" data-hscroll-id="my-custom-reference" style="visibility: hidden; opacity: 0;"></div>
 *
 */
class HorizontalScrollElement {

  constructor(el) {
    const self = this;

    this._el = el;
    this._targetReferenceId = el.getAttribute('data-hscroll-id');

    if ( this._targetReferenceId === null ) {
      throw new Error('Missing hscroll reference.');
    }

    this._targetReferenceEl = document.querySelector('.js-hscroll-reference[data-hscroll-id="'+ this._targetReferenceId +'"]');

    if ( this._targetReferenceEl === null ) {
      throw new Error('Missing hscroll reference element.');
    }


    // Event :: updates scrollable element's left padding when browser resizes
    this._handleLeftPaddingUpdateEvent = _debounce(() => {
      self.updatesElementLeftPadding();
    }, 200);
    window.addEventListener('resize', this._handleLeftPaddingUpdateEvent);

    // Updates padding on page load
    this.updatesElementLeftPadding();
  }

  calculateTargetReferenceLeftPositioning() {
    // Get the bounding rectangle of the target element relative to the viewport
    const rect = this._targetReferenceEl.getBoundingClientRect();

    // Retrieve computed styles of the element to access padding values
    const computedStyle = window.getComputedStyle(this._targetReferenceEl);

    // Parse the left padding value (in pixels); default to 0 if not set
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

    // Return the sum of the element's left position and its left padding
    // This gives the starting point of the content inside the element
    return rect.left + paddingLeft;
  }

  updatesElementLeftPadding() {
    // HACK
    // The recursive call with timeout allows code-split CSS to be loaded before attempting to make any calculations
    // as this utility relies on default left padding set in css to make them.
    // Elements are expected to always have left padding.
    if ( this.calculateTargetReferenceLeftPositioning() === 0 ) {
      const self = this;
      setTimeout(() => {
        self.updatesElementLeftPadding();
      }, 250);
      return;
    }

    this._el.style.paddingLeft = `${this.calculateTargetReferenceLeftPositioning()}px`;
  }
}

export default HorizontalScrollElement;
